/**
 * BoardCardMenu (DISPATCH-DETAIL slice 3, Seth 2026-06-24) — the right-click
 * context menu on a Pegasus board card: the dispatcher's quick actions without
 * opening the detail. Open dispatch · Reschedule · status changes · Open
 * customer. Status changes log to the activity stream. Closes on any click /
 * Esc / resize. ESC cards are read-only and don't get this.
 */
import { useEffect } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { ExternalLink, CalendarClock, PauseCircle, CheckCircle2, Ban, UserRound } from "lucide-react";
import { cn } from "@/lib/cn";
import { useSetTicketStatus, useLogDispatchEvent, type PendingTicket } from "./queue";

export function BoardCardMenu({
  job,
  x,
  y,
  onClose,
  onOpenDetail,
  onReschedule,
}: {
  job: PendingTicket;
  x: number;
  y: number;
  onClose: () => void;
  onOpenDetail: () => void;
  onReschedule: () => void;
}) {
  const navigate = useNavigate();
  const setStatus = useSetTicketStatus();
  const log = useLogDispatchEvent();

  useEffect(() => {
    const close = () => onClose();
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("click", close);
    window.addEventListener("keydown", onKey);
    window.addEventListener("resize", close);
    window.addEventListener("blur", close);
    return () => {
      window.removeEventListener("click", close);
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", close);
      window.removeEventListener("blur", close);
    };
  }, [onClose]);

  const changeStatus = (s: "on_hold" | "completed" | "cancelled", label: string) => {
    setStatus.mutate({ id: job.id, status: s });
    log.mutate({ dispatch_id: job.id, kind: s === "cancelled" ? "cancelled" : "status_changed", summary: `Status → ${label}` });
    onClose();
  };

  const Item = ({ icon: Icon, label, onClick, danger }: { icon: typeof ExternalLink; label: string; onClick: () => void; danger?: boolean }) => (
    <button
      type="button"
      onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); onClick(); }}
      className={cn("flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-black/[0.04]", danger ? "text-rose-600" : "text-slate-700")}
    >
      <Icon className="h-3.5 w-3.5 shrink-0" /> {label}
    </button>
  );

  return createPortal(
    <div
      role="menu"
      style={{ position: "fixed", top: Math.min(y, window.innerHeight - 240), left: Math.min(x, window.innerWidth - 200), zIndex: 1000 }}
      onMouseDown={(e) => e.stopPropagation()}
      data-glass="panel"
      className="w-48 overflow-hidden rounded-xl py-1"
    >
      <Item icon={ExternalLink} label="Open dispatch" onClick={() => { onOpenDetail(); onClose(); }} />
      <Item icon={CalendarClock} label="Reschedule…" onClick={() => { onReschedule(); onClose(); }} />
      <div className="my-1 border-t border-hairline" />
      <Item icon={PauseCircle} label="Mark on Hold" onClick={() => changeStatus("on_hold", "On Hold")} />
      <Item icon={CheckCircle2} label="Mark completed" onClick={() => changeStatus("completed", "Completed")} />
      <Item icon={Ban} label="Cancel dispatch" onClick={() => changeStatus("cancelled", "Cancelled")} danger />
      <div className="my-1 border-t border-hairline" />
      <Item icon={UserRound} label="Open customer" onClick={() => { if (job.custno) navigate(`/customers/${job.custno}`); onClose(); }} />
    </div>,
    document.body,
  );
}
